import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ModalUploadDeliveryNoteComponent } from '../../../../shared/modal-upload-delivery-note/modal-upload-delivery-note.component';
import { ModalPdfViewerComponent } from '../../../../shared/modal-pdf-viewer/modal-pdf-viewer.component';
import { ModalConfirmComponent } from '../../../../shared/modal-confirm/modal-confirm.component';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { PdfInformation } from 'src/app/models/service-detail/service-detail.model';
import { DeliveryNoteInfo } from 'src/app/models/delivery-note/delivery-note.model';
import { MenuAction } from 'src/app/models/common/common.model';
import { OrderPdfService } from 'src/app/services/alaran/order-pdf.service';
import { PdfKind } from 'src/app/enum/delivery-note/delivery-note-document-type.enum';

@Component({
  selector: 'app-delivery-note-section',
  templateUrl: './delivery-note-section.component.html',
  styleUrls: ['./delivery-note-section.component.css'],
})
export class DeliveryNoteSectionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() serviceId = '';
  @Input() orderHash = '';
  @Input() clientId = '';
  @Input() deliveryNoteData?: PdfInformation;
  @Output() deliveryNoteChanged = new EventEmitter<DeliveryNoteInfo | null>();

  isLoading = false;
  menuActions: MenuAction[] = [];
  readonly PdfKind = PdfKind;

  private readonly destroy$ = new Subject<void>();
  private modalRef?: NgbModalRef;

  constructor(
    private modalService: NgbModal,
    private bsModalService: BsModalService,
    private orderPdfService: OrderPdfService,
    private translateService: TranslateService,
    private alertService: TuiAlertService,
    private loader: LoaderService
  ) {}

  ngOnInit(): void {
    this.menuActions = this.buildMenuActions();
  }

  ngOnChanges(): void {
    this.menuActions = this.buildMenuActions();
  }

  private buildMenuActions(): MenuAction[] {
    const kind = this.deliveryNoteData?.pdfKind;

    if (kind === PdfKind.Url) {
      return [
        { icon: 'tuiIconTrash', labelKey: 'delivery-note.cancel_document', action: () => this.confirmDelete(), danger: true },
      ];
    }

    if (kind === PdfKind.DriverPdf) {
      return [
        { icon: 'tuiIconEye', labelKey: 'delivery-note.view_document', action: () => this.viewFile() },
        { icon: 'tuiIconDownload', labelKey: 'delivery-note.download_document', action: () => this.downloadFile() },
      ];
    }

    return [
      { icon: 'tuiIconEye', labelKey: 'delivery-note.view_document', action: () => this.viewFile() },
      { icon: 'tuiIconDownload', labelKey: 'delivery-note.download_document', action: () => this.downloadFile() },
      { icon: 'tuiIconTrash', labelKey: 'delivery-note.cancel_document', action: () => this.confirmDelete(), danger: true, dividerBefore: true },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasFile(): boolean {
    return !!this.deliveryNoteData;
  }

  get fileName(): string {
    return this.deliveryNoteData?.pdfName ?? '';
  }

  get fileUrl(): string {
    return this.deliveryNoteData?.pdfUrl ?? '';
  }

  get sectionTitleKey(): string {
    return this.deliveryNoteData?.pdfKind === PdfKind.DriverPdf
      ? 'delivery-note.delivery-note_section_title_driver'
      : 'delivery-note.delivery-note_section_title';
  }

  openUploadModal(): void {
    this.modalRef = this.modalService.open(ModalUploadDeliveryNoteComponent, {
      backdrop: 'static',
      centered: true,
      windowClass: 'modal-delivery-note-wide',
      size: 'lg',
    });

    this.modalRef.componentInstance.orderHash = this.orderHash;
    this.modalRef.componentInstance.serviceId = this.serviceId;
    this.modalRef.componentInstance.clientId = this.clientId;

    this.modalRef.result.then(
      (result: { deliveryNoteResult?: DeliveryNoteInfo }) => {
        if (result?.deliveryNoteResult) {
          this.assignDeliveryNoteToService(result.deliveryNoteResult);
        }
      },
      () => {}
    );
  }

  viewFile(): void {
    if (!this.fileUrl) return;
    this.bsModalService.show(ModalPdfViewerComponent, {
      class: 'modal-lg',
      initialState: {
        pdfUrl: this.fileUrl,
        pdfName: this.fileName,
        downloadLabelKey: 'delivery-note.download_document',
      },
    });
  }

  downloadFile(): void {
    if (!this.fileUrl) return;
    this.setLoading(true);
    fetch(this.fileUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Error downloading file');
       
        return res.blob().then(blob => {
          if (blob.type !== 'application/pdf') {
            return new Blob([blob], { type: 'application/pdf' });
          }
          return blob;
        });
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        const fileName = this.fileName.endsWith('.pdf') ? this.fileName : `${this.fileName}.pdf`;
        anchor.download = fileName;
        anchor.setAttribute('type', 'hidden');
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 100);
        this.showAlert('delivery-note.document_downloaded', 'success');
      })
      .catch((err) => {
        console.error('Error downloading delivery note:', err);
        this.showAlert('delivery-note.error_downloading_document', 'error');
      })
      .finally(() => this.setLoading(false));
  }

  confirmDelete(): void {
    if (!this.deliveryNoteData) return;

    const pdfId = this.deliveryNoteData.pdfId ?? this.deliveryNoteData.pdfUrl;
    if (!pdfId) return;

    const confirmModal = this.modalService.open(ModalConfirmComponent, { centered: true });
    confirmModal.componentInstance.title = this.translateService.instant('delivery-note.confirm_delete_title');
    confirmModal.componentInstance.message = this.translateService.instant('delivery-note.confirm_delete');

    confirmModal.result
      .then(() => this.deleteDeliveryNote(pdfId))
      .catch(() => {});
  }

  private assignDeliveryNoteToService(deliveryNote: DeliveryNoteInfo): void {
    this.setLoading(true);

    this.orderPdfService
      .uploadDeliveryNote(this.orderHash, deliveryNote.documentId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.setLoading(false))
      )
      .subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.deliveryNoteChanged.emit(deliveryNote);
            this.showAlert('delivery-note.upload_successful', 'success');
          } else {
            this.showAlert('delivery-note.error_uploading_document', 'error');
          }
        },
        error: (err) => {
          console.error('Error assigning delivery note:', err);
          this.showAlert('delivery-note.error_uploading_document', 'error');
        },
      });
  }

  private deleteDeliveryNote(pdfId: string): void {
    this.setLoading(true);

    this.orderPdfService
      .deleteDeliveryNote(this.orderHash, pdfId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.setLoading(false))
      )
      .subscribe({
        next: () => {
          this.deliveryNoteData = undefined;
          this.deliveryNoteChanged.emit(null);
          this.showAlert('delivery-note.document_deleted', 'success');
        },
        error: (err) => {
          console.error('Error deleting delivery note:', err);
          this.showAlert('delivery-note.error_deleting_document', 'error');
        },
      });
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    loading ? this.loader.show() : this.loader.hide();
  }

  private showAlert(key: string, status: 'info' | 'success' | 'error' | 'warning' = 'info'): void {
    this.alertService
      .open(this.translateService.instant(key), { status, autoClose: 5000 })
      .subscribe();
  }
}